import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n";
import { products } from "@/data/products";

const StoreContext = createContext(null);

export const useStore = () => useContext(StoreContext);

const productFromUrl = () => {
  const id = new URLSearchParams(window.location.search).get("produk");
  return products.find((p) => p.id === id) || null;
};

export function StoreProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("nb-lang") || "id");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(productFromUrl);

  useEffect(() => {
    localStorage.setItem("nb-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.id === product.id);
      if (found) {
        return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
      }
      return [...prev, { ...product, qty }];
    });
  };

  const updateQty = (id, delta) => {
    setCart((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0),
    );
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);

  const buyNow = (product, qty = 1) => {
    addToCart(product, qty);
    setCartOpen(false);
    setSelectedProduct(null);
    setCheckoutOpen(true);
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.qty * i.price, 0);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: translations[lang],
      cart,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      buyNow,
      cartCount,
      cartTotal,
      cartOpen,
      setCartOpen,
      checkoutOpen,
      setCheckoutOpen,
      selectedProduct,
      setSelectedProduct,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, cart, cartOpen, checkoutOpen, cartCount, cartTotal, selectedProduct],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
